import { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlatform } from "@/context/PlatformContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PaletteIcon, Upload, Loader2, Sparkles, Lock, Wand2 } from "lucide-react";
import { toast } from "sonner";

export default function Branding() {
  const { user, isOwner } = useAuth();
  const platform = usePlatform();
  const [b, setB] = useState(null);
  const [activeWs, setActiveWs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingWl, setSavingWl] = useState(false);
  const logoRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/branding");
    setB(data);
    try {
      const { data: wsList } = await api.get("/workspaces");
      const active = wsList.find((w) => w.id === user?.active_workspace_id) || wsList[0];
      setActiveWs(active);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.active_workspace_id]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/branding", {
        company_name: b.company_name,
        website: b.website,
        contact_phone: b.contact_phone,
        contact_email: b.contact_email,
        accent_color: b.accent_color,
      });
      setB(data);
      toast.success("Branding saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/branding/logo", fd);
      setB(data);
      toast.success("Logo uploaded");
    } catch {
      toast.error("Upload failed");
    }
  };

  const saveWhiteLabel = async (partial) => {
    if (!activeWs) return;
    setSavingWl(true);
    try {
      const { data } = await api.patch(`/workspaces/${activeWs.id}`, partial);
      setActiveWs(data);
      toast.success("White-label updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setSavingWl(false); }
  };

  if (!b) return <div className="p-12 text-zinc-500 text-sm">Loading…</div>;

  const canWhiteLabel = isOwner || ["agency", "enterprise"].includes(user?.tier);

  return (
    <div className="p-6 md:p-10 lg:p-14 max-w-[1200px]">
      <div className="mb-10">
        <div className="font-mono-tech text-[10px] text-amber-400 mb-3">STUDIO · BRANDING</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light leading-none">
          Brand <span className="font-bold text-amber-400">identity.</span>
        </h1>
        <p className="text-zinc-400 mt-4 max-w-2xl">
          Configure the logo, website and contact details that appear as overlays on every rendered video.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div>
            <Label className="font-mono-tech text-[10px] text-zinc-500">COMPANY NAME</Label>
            <Input
              data-testid="branding-name"
              value={b.company_name || ""}
              onChange={(e) => setB({ ...b, company_name: e.target.value })}
              className="mt-2 bg-[#0b0b0d] border-white/10 h-11"
            />
          </div>
          <div>
            <Label className="font-mono-tech text-[10px] text-zinc-500">WEBSITE</Label>
            <Input
              data-testid="branding-website"
              value={b.website || ""}
              onChange={(e) => setB({ ...b, website: e.target.value })}
              placeholder={platform.platform_domain}
              className="mt-2 bg-[#0b0b0d] border-white/10 h-11"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">CONTACT PHONE</Label>
              <Input
                data-testid="branding-phone"
                value={b.contact_phone || ""}
                onChange={(e) => setB({ ...b, contact_phone: e.target.value })}
                className="mt-2 bg-[#0b0b0d] border-white/10 h-11"
              />
            </div>
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">CONTACT EMAIL</Label>
              <Input
                data-testid="branding-email"
                value={b.contact_email || ""}
                onChange={(e) => setB({ ...b, contact_email: e.target.value })}
                className="mt-2 bg-[#0b0b0d] border-white/10 h-11"
              />
            </div>
          </div>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="save-branding"
            className="bg-amber-500 hover:bg-amber-600 text-black font-semibold h-11 mt-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save branding
          </Button>
        </div>

        <div>
          <Label className="font-mono-tech text-[10px] text-zinc-500 mb-3 block">LOGO</Label>
          <div
            onClick={() => logoRef.current?.click()}
            data-testid="logo-drop"
            className="border-2 border-dashed border-white/10 rounded-lg p-8 text-center cursor-pointer hover:border-amber-500/50 hover:bg-amber-500/5 transition-colors"
          >
            <input
              ref={logoRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])}
            />
            {b.logo_filename ? (
              <div>
                <img
                  src={fileUrl(`logo/${b.logo_filename}`)}
                  alt="Logo"
                  className="max-h-32 mx-auto"
                />
                <div className="mt-4 text-xs text-zinc-500">Click to replace</div>
              </div>
            ) : (
              <>
                <Upload className="w-10 h-10 text-amber-400 mx-auto mb-3" strokeWidth={1.5} />
                <div className="font-heading font-bold tracking-tight">Upload logo</div>
                <div className="text-xs text-zinc-500 mt-1">PNG · JPG · WebP · SVG</div>
                <div className="text-xs text-zinc-500 mt-2">Transparent PNG gives best result</div>
              </>
            )}
          </div>

          <div className="mt-6 p-4 bg-[#121214] border border-white/5 rounded-lg">
            <div className="font-mono-tech text-[10px] text-zinc-500 mb-3">OVERLAY · PREVIEW</div>
            <div className="text-xs text-zinc-400">
              Videos will show your logo bottom-right and
              <span className="text-amber-400"> {b.company_name} · {b.website}</span> bottom-left.
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Brand Kit (Studio+) ---------- */}
      <BrandKitSection
        activeWs={activeWs}
        onSaved={(ws) => setActiveWs(ws)}
        canUseBrandKit={isOwner || ["studio", "enterprise"].includes(user?.tier)}
      />

      {/* ---------- White-label (Agency+) ---------- */}
      <div className="mt-12 pt-10 border-t border-white/5">
        <div className="flex items-start gap-3 mb-6">
          <Sparkles className="w-5 h-5 text-amber-400 mt-1" strokeWidth={1.5} />
          <div className="flex-1">
            <div className="font-mono-tech text-[10px] text-amber-400 mb-1">WHITE · LABEL</div>
            <h2 className="font-heading text-2xl font-bold tracking-tight">Make it yours.</h2>
            <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
              Hide the &quot;MediaOthers · QOthers · Platform&quot; sidebar branding and show your workspace name instead.
              Perfect for agencies presenting the tool to their own clients.
            </p>
          </div>
          {!canWhiteLabel && (
            <div className="flex items-center gap-1.5 text-[10px] font-mono-tech text-zinc-500 px-2 py-1 rounded border border-white/10">
              <Lock className="w-3 h-3" strokeWidth={1.5} /> AGENCY+ ONLY
            </div>
          )}
        </div>

        <div className={`bg-[#121214] border rounded-lg p-6 space-y-5 ${canWhiteLabel ? "border-white/5" : "border-white/5 opacity-50 pointer-events-none"}`}>
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <div className="font-heading font-semibold tracking-tight mb-1">Hide MediaOthers branding</div>
              <div className="text-xs text-zinc-400">
                When enabled, the sidebar shows this workspace&apos;s name in place of &quot;MediaOthers&quot; and the QOthers tagline is hidden.
              </div>
            </div>
            <Switch
              checked={!!activeWs?.hide_platform_branding}
              onCheckedChange={(checked) => saveWhiteLabel({ hide_platform_branding: checked })}
              disabled={savingWl || !canWhiteLabel}
              data-testid="toggle-hide-branding"
            />
          </div>

          <div>
            <Label className="font-mono-tech text-[10px] text-zinc-500">CUSTOM FOOTER TEXT</Label>
            <Input
              data-testid="custom-footer"
              value={activeWs?.custom_footer_text || ""}
              onChange={(e) => setActiveWs({ ...activeWs, custom_footer_text: e.target.value })}
              onBlur={() => saveWhiteLabel({ custom_footer_text: activeWs?.custom_footer_text || "" })}
              placeholder="STUDIO · BY YOUR BRAND"
              className="mt-2 bg-[#0b0b0d] border-white/10 h-11 font-mono-tech uppercase text-xs"
              disabled={!canWhiteLabel}
            />
            <div className="text-[10px] text-zinc-500 mt-2">
              Appears under your workspace name in the sidebar. Auto-saves on blur.
            </div>
          </div>

          {!canWhiteLabel && (
            <a href="/pricing" className="inline-flex items-center gap-1.5 text-amber-400 text-sm font-medium hover:underline">
              Upgrade to Agency → unlock white-label
            </a>
          )}
        </div>

        {/* Custom domain — Enterprise only */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-3">
            <div className="font-mono-tech text-[10px] text-amber-400">CUSTOM · DOMAIN</div>
            <span className="font-mono-tech text-[8px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 uppercase">
              Enterprise
            </span>
          </div>
          <div className={`bg-[#121214] border rounded-lg p-6 space-y-4 ${
            (isOwner || user?.tier === "enterprise") ? "border-white/5" : "border-white/5 opacity-50 pointer-events-none"
          }`}>
            <Label className="font-mono-tech text-[10px] text-zinc-500">DOMAIN (e.g. studio.your-brand.com)</Label>
            <Input
              data-testid="custom-domain"
              value={activeWs?.custom_domain || ""}
              onChange={(e) => setActiveWs({ ...activeWs, custom_domain: e.target.value.toLowerCase() })}
              onBlur={() => saveWhiteLabel({ custom_domain: activeWs?.custom_domain || "" })}
              placeholder="studio.your-brand.com"
              className="bg-[#0b0b0d] border-white/10 h-11 font-mono-tech text-xs"
              disabled={!(isOwner || user?.tier === "enterprise")}
            />
            {activeWs?.custom_domain ? (
              <div className="p-3 bg-amber-500/[0.06] border border-amber-500/20 rounded-md text-xs text-zinc-300">
                <div className="font-semibold text-amber-400 mb-1">One more step — DNS setup</div>
                Add this CNAME record at your DNS provider so your custom domain points at {platform.platform_name}:
                <div className="mt-2 p-2 bg-[#0b0b0d] rounded font-mono-tech text-[11px]">
                  <div>Type: <span className="text-emerald-400">CNAME</span></div>
                  <div>Host: <span className="text-emerald-400">{activeWs.custom_domain.split(".")[0]}</span></div>
                  <div>Target: <span className="text-emerald-400">{platform.custom_domain_cname_target}</span></div>
                </div>
                <div className="mt-2 text-zinc-500">
                  After the DNS record propagates (usually 5-15 min), contact {platform.support_email} with your domain so we can provision the SSL certificate.
                </div>
              </div>
            ) : (
              <div className="text-[11px] text-zinc-500">
                Serve {platform.platform_name} from your own subdomain instead of the default. Requires DNS access + SSL provisioning by our support team.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


/* ---------- Brand Kit (Studio+) ---------- */
function BrandKitSection({ activeWs, onSaved, canUseBrandKit }) {
  const [form, setForm] = useState({ brand_tagline: "", brand_tone: "", brand_summary: "" });
  const [saving, setSaving] = useState(false);
  const [scanUrl, setScanUrl] = useState("");
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    if (activeWs) {
      setForm({
        brand_tagline: activeWs.brand_tagline || "",
        brand_tone: activeWs.brand_tone || "",
        brand_summary: activeWs.brand_summary || "",
      });
      setScanUrl(activeWs.website || "");
    }
  }, [activeWs?.id, activeWs?.brand_tagline, activeWs?.brand_tone, activeWs?.brand_summary, activeWs?.website]);

  if (!activeWs) return null;

  const saveBrandKit = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/workspaces/${activeWs.id}`, form);
      onSaved(data);
      toast.success("Brand Kit saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const scanWebsite = async () => {
    const url = scanUrl.trim();
    if (!url) { toast.error("Enter a website URL first"); return; }
    setScanning(true);
    try {
      const { data } = await api.post("/brand-kit/scan-website", { url });
      setForm({
        brand_tagline: data.brand_tagline || "",
        brand_tone: data.brand_tone || "",
        brand_summary: data.brand_summary || "",
      });
      // Bubble the updated workspace object up so the parent state stays in sync.
      onSaved({ ...activeWs, ...data });
      toast.success(`Scanned ${data.source_title || "site"} — kit auto-filled`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="mt-12 pt-10 border-t border-white/5" data-testid="brand-kit-section">
      <div className="flex items-start gap-3 mb-6">
        <PaletteIcon className="w-5 h-5 text-amber-400 mt-1" strokeWidth={1.5} />
        <div className="flex-1">
          <div className="font-mono-tech text-[10px] text-amber-400 mb-1">AI · BRAND · KIT</div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">One voice, everywhere.</h2>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
            Set your brand story, tagline and tone once — every AI Blog post and AI Social caption in this workspace
            automatically writes in your voice. Feels less like a template, more like your best copywriter.
          </p>
        </div>
        {!canUseBrandKit && (
          <div className="flex items-center gap-1.5 text-[10px] font-mono-tech text-zinc-500 px-2 py-1 rounded border border-white/10">
            <Lock className="w-3 h-3" strokeWidth={1.75} /> STUDIO+ ONLY
          </div>
        )}
        {canUseBrandKit && (
          <div className="text-[10px] font-mono-tech text-emerald-400 px-2 py-1 rounded border border-emerald-500/30 bg-emerald-500/10">
            ACTIVE
          </div>
        )}
      </div>

      <div className={`space-y-4 ${canUseBrandKit ? "" : "opacity-50 pointer-events-none select-none"}`}>
        {/* One-click scanner */}
        <div className="p-4 rounded-lg border border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-transparent">
          <div className="flex items-center gap-2 mb-2">
            <div className="font-mono-tech text-[10px] text-amber-400">ONE · CLICK · SCANNER</div>
            <div className="text-[10px] font-mono-tech text-zinc-500">· costs 5 credits</div>
          </div>
          <div className="flex flex-col md:flex-row gap-2">
            <Input
              data-testid="brand-scan-url"
              value={scanUrl}
              onChange={(e) => setScanUrl(e.target.value)}
              placeholder="https://yourcompany.com"
              className="flex-1 bg-[#0b0b0d] border-white/10 h-10"
            />
            <Button
              type="button"
              onClick={scanWebsite}
              disabled={scanning || !canUseBrandKit}
              data-testid="scan-brand-website"
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold h-10 min-w-[160px]"
            >
              {scanning ? <><Loader2 className="w-4 h-4 animate-spin mr-2" strokeWidth={2} /> Scanning…</> : <><Wand2 className="w-4 h-4 mr-2" strokeWidth={1.75} /> Auto-fill from site</>}
            </Button>
          </div>
          <div className="text-[10px] text-zinc-500 mt-2">
            Reads your homepage and drafts a tagline, tone and story you can tweak before saving.
          </div>
        </div>

        <div>
          <Label className="font-mono-tech text-[10px] text-zinc-500">TAGLINE</Label>
          <Input
            data-testid="brand-tagline"
            value={form.brand_tagline}
            onChange={(e) => setForm({ ...form, brand_tagline: e.target.value })}
            placeholder="e.g. Cinematic real-estate in one click."
            maxLength={120}
            className="mt-2 bg-[#0b0b0d] border-white/10 h-11"
          />
        </div>
        <div>
          <Label className="font-mono-tech text-[10px] text-zinc-500">VOICE · TONE</Label>
          <Input
            data-testid="brand-tone"
            value={form.brand_tone}
            onChange={(e) => setForm({ ...form, brand_tone: e.target.value })}
            placeholder="e.g. warm, no jargon, expert but approachable"
            maxLength={200}
            className="mt-2 bg-[#0b0b0d] border-white/10 h-11"
          />
        </div>
        <div>
          <Label className="font-mono-tech text-[10px] text-zinc-500">BRAND · SUMMARY</Label>
          <textarea
            data-testid="brand-summary"
            value={form.brand_summary}
            onChange={(e) => setForm({ ...form, brand_summary: e.target.value })}
            rows={5}
            placeholder="Who you are, who you serve and what makes you different. 2-4 sentences."
            maxLength={1200}
            className="mt-2 w-full bg-[#0b0b0d] border border-white/10 rounded-md p-3 text-sm resize-none focus:outline-none focus:border-amber-500/50"
          />
          <div className="text-[10px] font-mono-tech text-zinc-500 mt-1">{form.brand_summary.length}/1200</div>
        </div>
        <Button
          onClick={saveBrandKit}
          disabled={saving || !canUseBrandKit}
          data-testid="save-brand-kit"
          className="bg-amber-500 hover:bg-amber-600 text-black font-semibold h-11"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Save Brand Kit
        </Button>
      </div>
    </div>
  );
}
